import { useParams } from "react-router-dom";

import { Button, Card, CircularProgress } from "@mui/material";
import { Form, Formik } from "formik";

import { Translate } from "@/components/Application/Translate";
import { AlertToast } from "@/helpers/alerts/AlertToast";
import CustomDropZoneUpload from "@/helpers/components/CustomFormFields/CustomDropZoneUpload";

import { UploadProcedureNodeDocumentRequest } from "@/model/Procedure/ProcedureNodeDependency";

import { UploadProcedureNodeDocument } from "@/service/ProcedureMutations";
import { useFetchDocumentForCurrentNode } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

type Props = {
  onClose: () => void;
  nodeId: number;
  isUploading: boolean;
  onComplete: () => void;
};

const NodeDocumentUpload = (props: Props) => {
  const { id: ProcedureId } = useParams<RouteParams>();
  const { onClose, nodeId, onComplete } = props;

  const { isLoading, data } = useFetchDocumentForCurrentNode({
    ProcedureId: Number(ProcedureId),
    SchemeId: nodeId,
  });

  const uploadProcedureNodeDocument = UploadProcedureNodeDocument();

  const handleSave = async (values: UploadProcedureNodeDocumentRequest) => {
    // Guard: require a real File
    if (!values.File) {
      AlertToast.failed?.(); // or a more specific toast
      return;
    }

    const payload: UploadProcedureNodeDocumentRequest = {
      ...values,
      FileName: values.File.name,
      ExternalUniqueId: "", // uploading a new local file
    };

    try {
      await uploadProcedureNodeDocument(payload);
      AlertToast.success();
      onClose();
      onComplete();
    } catch {
      AlertToast.failed();
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ProcedureId: Number(ProcedureId),
        SchemeId: nodeId,
        File: undefined as File | undefined,        // <-- don't seed from API
        FileName: data?.Document?.FileName ?? "",   // optional, shown if you use it
        ExternalUniqueId: "",                       // uploading local file
      }}
      onSubmit={handleSave}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Form style={{ marginBottom: 0 }}>
          <Card
            elevation={2}
            style={{
              padding: "1rem",
              textAlign: "center",
              height: "300px",
            }}
          >
            <CustomDropZoneUpload
              name="File"
              translationString="File.Dropzone"
              id="File"
              type="file"
              setFieldValue={setFieldValue}
            />
          </Card>

          <Button
            variant="outlined"
            color="success"
            sx={{ margin: "1rem", position: "fixed", top: 50, right: 100 }}
            type="submit"
            // disable only when busy OR no file selected
            disabled={
              isSubmitting || isLoading || props.isUploading || !values.File
            }
            startIcon={
              isSubmitting || isLoading || props.isUploading ? (
                <CircularProgress size={20} />
              ) : null
            }
          >
            <Translate String="Procedure.Button.Save">Save</Translate>
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default NodeDocumentUpload;
